/**
 * simple_id → canonical salon id, through the edge (#31).
 *
 * The whole surviving API surface of the deprecated /salon/:slug/:simpleId
 * route. Its old loader fanned out to five runapis endpoints directly —
 * the storefront's last direct-to-runapis reads, in violation of the
 * standing rule that ALL vitrina traffic goes through the edge, and a
 * bypass of the edge's gate (a salon that turned its vitrina off stayed
 * readable through the legacy URL).
 *
 * Now: one cacheable edge call resolves the canonical id, and the caller
 * hands off to the exact clean-slug path. When the legacy route is retired
 * (the issue's preferred endgame, pending traffic data), this file goes
 * with it.
 */

import { getVitrinaApiBase } from '@/js/utils/environment.js';

/**
 * Resolve a legacy simple id to the canonical 16-digit salon id.
 *
 * @param {number|string} simpleId  the short numeric id from the legacy URL
 * @returns {Promise<string|null>}  canonical salon id, or null when the
 *   salon does not exist or is not publicly available — the two cases are
 *   deliberately indistinguishable here, matching the edge's uniform miss.
 */
export async function resolveSimpleIdToSalonId(simpleId) {
  const base = getVitrinaApiBase();
  const response = await fetch(`${base}/api/salon-info/${encodeURIComponent(simpleId)}`);
  if (!response.ok) return null;

  const payload = await response.json();
  if (!payload?.ok) return null;

  // `sid` is the aliased canonical id on the PII-free public projection.
  return payload.data?.sid || payload.data?.salon_id || null;
}
